// Portal — Appointments Page (Live API data)
import { useState, useEffect } from 'react';
import { Calendar, Clock, MapPin, Video, Stethoscope, Plus, RefreshCw } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Appointment {
    id: string;
    scheduledStart: string;
    scheduledEnd: string;
    serviceType: string;
    appointmentType: string;
    status: string;
    chiefComplaint: string | null;
    doctor: { firstName: string; lastName: string; specialty: string | null; calendarColor: string };
    location: { name: string } | null;
}

const statusMap: Record<string, { label: string; cls: string }> = {
    booked: { label: 'Agendada', cls: 'portal-badge-neutral' },
    confirmed: { label: 'Confirmada', cls: 'portal-badge-primary' },
    arrived: { label: 'Llegó', cls: 'portal-badge-primary' },
    in_progress: { label: 'En consulta', cls: 'portal-badge-primary' },
    completed: { label: 'Completada', cls: 'portal-badge-success' },
    cancelled: { label: 'Cancelada', cls: 'portal-badge-danger' },
    no_show: { label: 'No asistió', cls: 'portal-badge-danger' },
};

import { API_BASE } from '../config/api';

export function AppointmentsPage() {
    const [appointments, setAppointments] = useState<Appointment[]>([]);
    const [loading, setLoading] = useState(true);
    const [tab, setTab] = useState<'upcoming' | 'past'>('upcoming');

    useEffect(() => { loadAppointments(); }, []);

    async function loadAppointments() {
        setLoading(true);
        try {
            // Get the first patient for demo context
            const ptRes = await fetch(`${API_BASE}/patients?limit=1`);
            const ptJson = await ptRes.json();
            const pt = ptJson.data?.[0];
            if (!pt) { setLoading(false); return; }

            const res = await fetch(`${API_BASE}/appointments?patientId=${pt.id}&limit=50`);
            const json = await res.json();
            setAppointments(json.data || []);
        } catch {
            console.warn('API unavailable');
        } finally {
            setLoading(false);
        }
    }

    const now = new Date();
    const upcoming = appointments
        .filter(a => new Date(a.scheduledStart) >= now && a.status !== 'cancelled' && a.status !== 'completed')
        .sort((a, b) => new Date(a.scheduledStart).getTime() - new Date(b.scheduledStart).getTime());
    const past = appointments
        .filter(a => new Date(a.scheduledStart) < now || a.status === 'completed' || a.status === 'cancelled')
        .sort((a, b) => new Date(b.scheduledStart).getTime() - new Date(a.scheduledStart).getTime());

    const display = tab === 'upcoming' ? upcoming : past;

    return (
        <div className="fade-in">
            <div className="portal-header">
                <h1>Mis Citas</h1>
                <p>{loading ? '...' : `${upcoming.length} citas próximas`}</p>
            </div>

            <div className="portal-content">
                <Link to="/book" style={{ textDecoration: 'none' }}>
                    <button className="portal-btn portal-btn-primary" style={{ marginBottom: 16, width: '100%' }} id="book-appointment-btn">
                        <Plus size={18} /> Agendar nueva cita
                    </button>
                </Link>

                {/* Tabs */}
                <div style={{ display: 'flex', gap: 0, marginBottom: 16, borderRadius: 'var(--radius-md)', overflow: 'hidden', border: '1px solid var(--color-primary-100)' }}>
                    <button onClick={() => setTab('upcoming')} style={{
                        flex: 1, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                        background: tab === 'upcoming' ? 'var(--color-primary)' : 'transparent',
                        color: tab === 'upcoming' ? 'white' : 'var(--color-primary)',
                        border: 'none',
                    }}>
                        Próximas ({upcoming.length})
                    </button>
                    <button onClick={() => setTab('past')} style={{
                        flex: 1, padding: '10px 16px', fontSize: 14, fontWeight: 600, cursor: 'pointer',
                        background: tab === 'past' ? 'var(--color-primary)' : 'transparent',
                        color: tab === 'past' ? 'white' : 'var(--color-primary)',
                        border: 'none',
                    }}>
                        Historial ({past.length})
                    </button>
                </div>

                {loading ? (
                    Array.from({ length: 3 }).map((_, i) => (
                        <div key={i} className="portal-card"><div className="skeleton" style={{ height: 80 }} /></div>
                    ))
                ) : display.length === 0 ? (
                    <div className="empty-state" style={{ textAlign: 'center', padding: 40 }}>
                        <Calendar size={48} color="var(--color-text-muted)" />
                        <p style={{ color: 'var(--color-text-secondary)', marginTop: 12 }}>
                            {tab === 'upcoming' ? 'No tiene citas programadas' : 'No hay historial de citas'}
                        </p>
                    </div>
                ) : (
                    display.map(appt => {
                        const st = statusMap[appt.status] ?? { label: appt.status, cls: 'portal-badge-neutral' };
                        const date = new Date(appt.scheduledStart);
                        const time = date.toLocaleTimeString('es-CO', { hour: '2-digit', minute: '2-digit' });

                        return (
                            <div key={appt.id} className="portal-card" style={{ display: 'flex', gap: 14 }}>
                                {/* Date block */}
                                <div style={{
                                    minWidth: 56, textAlign: 'center',
                                    background: 'var(--color-primary-50)', borderRadius: 'var(--radius-md)',
                                    padding: '10px 8px',
                                }}>
                                    <div style={{ fontSize: 22, fontWeight: 700, color: 'var(--color-primary)' }}>
                                        {date.getDate()}
                                    </div>
                                    <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--color-primary-dark)', textTransform: 'uppercase' }}>
                                        {date.toLocaleDateString('es-CO', { month: 'short' })}
                                    </div>
                                </div>

                                {/* Details */}
                                <div style={{ flex: 1 }}>
                                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                                        <div style={{ fontWeight: 600, display: 'flex', alignItems: 'center', gap: 6 }}>
                                            <span style={{ width: 8, height: 8, borderRadius: '50%', background: appt.doctor.calendarColor, display: 'inline-block' }} />
                                            Dr. {appt.doctor.firstName} {appt.doctor.lastName}
                                        </div>
                                        <span className={`portal-badge ${st.cls}`}>{st.label}</span>
                                    </div>
                                    <div style={{ fontSize: 13, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                                        {appt.doctor.specialty || 'Medicina General'}
                                    </div>
                                    {appt.chiefComplaint && (
                                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 2 }}>
                                            Motivo: {appt.chiefComplaint}
                                        </div>
                                    )}
                                    <div style={{ display: 'flex', gap: 12, fontSize: 13, color: 'var(--color-text-muted)', marginTop: 8 }}>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <Clock size={13} /> {time}
                                        </span>
                                        <span style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
                                            {appt.appointmentType === 'telehealth' ? <Video size={13} /> : <Stethoscope size={13} />}
                                            {appt.appointmentType === 'telehealth' ? 'Teleconsulta' : 'Presencial'}
                                        </span>
                                    </div>
                                    {appt.location && (
                                        <div style={{ fontSize: 12, color: 'var(--color-text-muted)', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}>
                                            <MapPin size={12} /> {appt.location.name}
                                        </div>
                                    )}

                                    {appt.appointmentType === 'telehealth' && appt.status !== 'cancelled' && appt.status !== 'completed' && (
                                        <Link to="/telehealth">
                                            <button className="portal-btn portal-btn-primary" style={{ marginTop: 12, padding: '10px 16px', fontSize: 14 }}>
                                                <Video size={16} /> Unirse a teleconsulta
                                            </button>
                                        </Link>
                                    )}
                                </div>
                            </div>
                        );
                    })
                )}
            </div>
        </div>
    );
}
